import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getClassProgress, getClasses, getClassStudents, updateAssignmentVisibility } from '../../services/api';
import './ClassProgress.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';

const ClassProgress = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [classData, setClassData] = useState(null);
  const [classProgress, setClassProgress] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classId || '');
  const [editingVisibility, setEditingVisibility] = useState(null);
  const [visibilityValue, setVisibilityValue] = useState('');

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        // Fetch classes list
        const classesResponse = await handleRequest(getClasses);
        const classesList = classesResponse.data || [];
        setClasses(classesList);

        // If classId is provided, fetch that class's progress
        if (classId) {
          await loadClassProgress(classId, classesList);
        } else if (classesList.length > 0) {
          // If no classId but classes exist, select the first one
          const firstClassId = classesList[0]._id || classesList[0].id;
          setSelectedClassId(firstClassId);
          await loadClassProgress(firstClassId, classesList);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn klassedata');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate, handleRequest, classId]);

  const loadClassProgress = async (id, classesList = null) => {
    try {
      const [studentsResponse, progressResponse] = await Promise.all([
        handleRequest(getClassStudents, id).catch(() => ({ data: [] })),
        handleRequest(getClassProgress, id).catch(() => ({ data: null }))
      ]);

      // Find class data
      const allClasses = classesList || classes;
      const classInfo = allClasses.find(c => (c._id || c.id) === id);
      
      if (!classInfo && !classesList) {
        // If not found, fetch classes again
        const classesResponse = await handleRequest(getClasses);
        const updatedClasses = classesResponse.data || [];
        const foundClass = updatedClasses.find(c => (c._id || c.id) === id);
        setClassData(foundClass);
      } else {
        setClassData(classInfo);
      }
      
      // The API returns { classroom, totals, students, assignments }
      // Transform it to match the expected format
      const progressData = progressResponse.data;
      if (progressData) {
        const students = progressData.students || [];
        const avgScore = students.length > 0
          ? Math.round(students.reduce((sum, s) => sum + (s.quiz?.avgScore || 0), 0) / students.length)
          : 0;
        
        setClassProgress({
          totalStudents: progressData.totals?.students || students.length || 0,
          completedLessons: progressData.totals?.assignments || 0,
          averageScore: avgScore,
          totalQuizAttempts: students.reduce((sum, s) => sum + (s.quiz?.attempts || 0), 0),
          students: students,
          assignments: progressData.assignments || []
        });
      } else {
        setClassProgress(null);
      }
      setSelectedClassId(id);
      
      // Update URL without navigation
      window.history.replaceState(null, '', `/classes/${id}/progress`);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke laste inn klassefremdrift');
    }
  };

  const handleClassChange = (e) => {
    const newClassId = e.target.value;
    if (newClassId) {
      loadClassProgress(newClassId, classes);
    }
  };

  const formatVisibilityLabel = (visibleFrom) => {
    if (!visibleFrom) return 'Synlig nå';
    const parsed = new Date(visibleFrom);
    if (Number.isNaN(parsed.getTime())) return 'Synlig nå';
    const now = new Date();
    if (parsed > now) {
      return `Synlig fra ${parsed.toLocaleString('no-NO')}`;
    }
    return 'Synlig nå';
  };

  const isFutureAssignment = (visibleFrom) => {
    if (!visibleFrom) return false;
    const parsed = new Date(visibleFrom);
    if (Number.isNaN(parsed.getTime())) return false;
    return parsed > new Date();
  };

  const handleEditVisibility = (assignment) => {
    setEditingVisibility(assignment.assignmentId);
    if (assignment.visibleFrom) {
      const date = new Date(assignment.visibleFrom);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setVisibilityValue(localDateTime);
    } else {
      setVisibilityValue('');
    }
  };

  const handleSaveVisibility = async (assignmentId) => {
    try {
      const visibleFrom = visibilityValue ? new Date(visibilityValue).toISOString() : null;
      await handleRequest(updateAssignmentVisibility, assignmentId, visibleFrom);
      
      // Reload class progress
      if (selectedClassId) {
        await loadClassProgress(selectedClassId, classes);
      }
      setEditingVisibility(null);
      setVisibilityValue('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke oppdatere synlighet');
    }
  };

  const handleCancelEdit = () => {
    setEditingVisibility(null);
    setVisibilityValue('');
  };

  if (loading) {
    return (
      <div className="class-progress-container">
        <div className="loading">Laster klassefremdrift...</div>
      </div>
    );
  }

  return (
    <div className="class-progress-container">
      <div className="class-progress-header">
        <div>
          <h1>Klassefremdrift</h1>
          {classData && (
            <p className="class-name">{classData.name}</p>
          )}
        </div>
        <button 
          className="back-btn"
          onClick={() => navigate('/dashboard')}
        >
          ← Tilbake til oversikten
        </button>
      </div>

      {errorMessage && <ErrorCard message={errorMessage} />}

      <div className="class-progress-content">
        {classes.length > 0 ? (
          <>
            <div className="class-selector-section">
              <label htmlFor="class-select">Velg klasse:</label>
              <select
                id="class-select"
                value={selectedClassId}
                onChange={handleClassChange}
                className="class-select"
              >
                <option value="">Velg en klasse...</option>
                {classes.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {classProgress ? (
              <>
                {/* Overview Stats */}
                <div className="progress-overview-section">
                  <h2>Oversikt</h2>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-label">Antall elever</div>
                      <div className="stat-value">{classProgress.totalStudents || 0}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Fullførte leksjoner</div>
                      <div className="stat-value">{classProgress.completedLessons || 0}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Gjennomsnittlig quizscore</div>
                      <div className="stat-value">
                        {classProgress.averageScore ? `${classProgress.averageScore}%` : 'N/A'}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Antall quizforsøk</div>
                      <div className="stat-value">{classProgress.totalQuizAttempts || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Assignments Section */}
                {classProgress.assignments && classProgress.assignments.length > 0 && (
                  <div className="assignments-section">
                    <h2>Tildelte leksjoner</h2>
                    {classProgress.assignments.map((assignment) => {
                      const isFuture = isFutureAssignment(assignment.visibleFrom);
                      return (
                      <div key={assignment.assignmentId} className={`assignment-card ${isFuture ? 'future-assignment' : ''}`}>
                        <div className="assignment-header">
                          <div>
                            <h3>
                              {assignment.resourceId?.title || 'Leksjon uten tittel'}
                              {isFuture && <span className="future-badge" title="Fremtidig tildeling">Fremtidig</span>}
                            </h3>
                            <div className="assignment-meta">
                              <span className="resource-type-badge">
                                {assignment.resourceType === 'Lesson' ? 'Quiz' : 'Programmering'}
                              </span>
                              {assignment.message && (
                                <span className="assignment-message">{assignment.message}</span>
                              )}
                            </div>
                            <div className="assignment-visibility-section">
                              <span className="visibility-label" title={formatVisibilityLabel(assignment.visibleFrom)}>
                                <strong>Synlighet:</strong> {formatVisibilityLabel(assignment.visibleFrom)}
                              </span>
                              {editingVisibility === assignment.assignmentId ? (
                                <div className="visibility-edit">
                                  <input
                                    type="datetime-local"
                                    value={visibilityValue}
                                    onChange={(e) => setVisibilityValue(e.target.value)}
                                    className="visibility-input"
                                  />
                                  <div className="visibility-edit-actions">
                                    <button
                                      className="save-btn"
                                      onClick={() => handleSaveVisibility(assignment.assignmentId)}
                                      title="Lagre"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      className="cancel-btn"
                                      onClick={handleCancelEdit}
                                      title="Avbryt"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="edit-visibility-btn"
                                  onClick={() => handleEditVisibility(assignment)}
                                  title="Rediger synlighet"
                                >
                                  Rediger synlighet
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="assignment-stats">
                            <div className="assignment-stat">
                              <span className="stat-label">Fullført:</span>
                              <span className="stat-value">{assignment.completedCount}/{assignment.totalAssigned}</span>
                            </div>
                            <div className="assignment-stat">
                              <span className="stat-label">Fullføringsgrad:</span>
                              <span className="stat-value">{assignment.completionRate}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="assignment-progress-bar">
                          <div 
                            className="assignment-progress-fill"
                            style={{ width: `${assignment.completionRate}%` }}
                          />
                        </div>

                        <div className="assignment-student-list">
                          <div className="student-list-header">
                            <span>Elever som trenger oppfølging ({assignment.needsFollowUpCount})</span>
                            <span>Elever som gjør det bra ({assignment.doingWellCount})</span>
                          </div>
                          <div className="results-table-container">
                            <table className="results-table assignment-table">
                              <thead>
                                <tr>
                                  <th>Elev</th>
                                  <th>Status</th>
                                  <th>Resultat</th>
                                  <th>Forsøk</th>
                                  <th>Fremdrift</th>
                                </tr>
                              </thead>
                              <tbody>
                                {assignment.studentProgress.map((sp) => (
                                  <tr 
                                    key={sp.studentId}
                                    className={
                                      sp.doingWell ? 'student-doing-well' : 
                                      sp.needsFollowUp ? 'student-needs-followup' : 
                                      sp.isCompleted ? 'student-completed' : ''
                                    }
                                  >
                                    <td className="student-name">
                                      {sp.studentName}
                                      <span className="student-email-small">{sp.studentEmail}</span>
                                    </td>
                                    <td>
                                      {sp.isCompleted ? (
                                        <span className="status-badge status-completed">Fullført</span>
                                      ) : sp.needsFollowUp ? (
                                        <span className="status-badge status-followup">Trenger oppfølging</span>
                                      ) : (
                                        <span className="status-badge status-pending">Ikke startet</span>
                                      )}
                                    </td>
                                    <td>
                                      {assignment.resourceType === 'Lesson' ? (
                                        sp.score ? `${sp.score}%` : 'N/A'
                                      ) : (
                                        sp.totalTasks > 0 ? `${sp.completedTasks}/${sp.totalTasks} oppgaver` : 'N/A'
                                      )}
                                    </td>
                                    <td>{sp.attempts || 0}</td>
                                    <td>
                                      {assignment.resourceType === 'ProgrammingLesson' && sp.totalTasks > 0 ? (
                                        <div className="task-progress-bar">
                                          <div 
                                            className="task-progress-fill"
                                            style={{ width: `${(sp.completedTasks / sp.totalTasks) * 100}%` }}
                                          />
                                          <span className="task-progress-text">
                                            {sp.completedTasks}/{sp.totalTasks}
                                          </span>
                                        </div>
                                      ) : (
                                        sp.isCompleted ? '100%' : '0%'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Student Progress Table */}
                {classProgress.students && classProgress.students.length > 0 ? (
                  <div className="student-progress-section">
                    <h2>Elevoversikt</h2>
                    <div className="results-table-container">
                      <table className="results-table">
                        <thead>
                          <tr>
                            <th>Elev</th>
                            <th>E-post</th>
                            <th>Quizforsøk</th>
                            <th>Snitt quizscore</th>
                            <th>Prog. fullførte oppgaver</th>
                            <th>Prog. innleveringer</th>
                            <th>Tildelte fullført</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classProgress.students.map((row) => (
                            <tr 
                              key={row.student._id || row.student.id}
                              className={`clickable-row ${
                                row.doingWell ? 'student-doing-well' : 
                                row.needsFollowUp ? 'student-needs-followup' : ''
                              }`}
                              onClick={() => navigate(`/students/${row.student._id || row.student.id}/progress`)}
                              style={{ cursor: 'pointer' }}
                              title="Klikk for å se elevens fremdrift"
                            >
                              <td className="student-name">{row.student.name}</td>
                              <td className="student-email">{row.student.email}</td>
                              <td>{row.quiz?.attempts || 0}</td>
                              <td>{row.quiz?.avgScore ? `${row.quiz.avgScore}%` : 'N/A'}</td>
                              <td>{row.programming?.completedTasks || 0}</td>
                              <td>{row.programming?.submissions || 0}</td>
                              <td>
                                {row.assignments?.completedAssignedCount || 0}/
                                {row.assignments?.assignedCount || 0}
                              </td>
                              <td>
                                {row.doingWell && (
                                  <span className="status-badge status-doing-well">Gjør det bra</span>
                                )}
                                {row.needsFollowUp && (
                                  <span className="status-badge status-followup">Trenger oppfølging</span>
                                )}
                                {!row.doingWell && !row.needsFollowUp && row.assignments?.assignedCount > 0 && (
                                  <span className="status-badge status-ok">På rett spor</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="no-progress">
                    <p>Ingen fremdrift registrert ennå for denne klassen.</p>
                  </div>
                )}
              </>
            ) : selectedClassId ? (
              <div className="no-progress">
                <p>Ingen fremdriftsdata tilgjengelig for denne klassen.</p>
              </div>
            ) : (
              <div className="no-progress">
                <p>Velg en klasse for å se fremdrift.</p>
              </div>
            )}
          </>
        ) : (
          <div className="no-progress">
            <p>Ingen klasser funnet. Opprett en klasse for å begynne å følge med på fremdrift.</p>
            <button 
              className="create-class-btn"
              onClick={() => navigate('/dashboard')}
            >
              Gå til oversikten
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassProgress;
